import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getUserLocalePreference } from '../../../../../common/users/utils/userPropertyUtils';
import { getIsFeatureEnabledForCurrentUser } from '../../../../element/feature/elementFeatureSelector';
import { ExperimentId } from '../../../../../common/experiments/experimentsConstants';
import { getCurrentUser } from '../../../currentUserSelector';
import { isUserAvailableSelector } from '../../../currentUserSelector';
import i18n, { switchToCdnBackend, isUsingCdn } from '../../../../i18n/initI18n';

/**
 * Manager component that initializes i18n language based on user preferences
 * and switches between local and CDN backends based on feature flags
 * Should be mounted after user data is available
 */
const I18nLanguageManager = () => {
    const currentUser = useSelector(getCurrentUser);
    const isUserAvailable = useSelector(isUserAvailableSelector);
    const isSpanishLocaleEnabled = useSelector(getIsFeatureEnabledForCurrentUser(ExperimentId.spanishLocale));
    const hasInitialized = useRef(false);

    useEffect(() => {
        // Only run initialization once when user is available
        if (!isUserAvailable || hasInitialized.current) {
            return;
        }

        const initializeI18n = async () => {
            // Switch to CDN backend if Spanish locale feature is enabled
            if (isSpanishLocaleEnabled && !isUsingCdn()) {
                try {
                    await switchToCdnBackend();
                } catch (error) {
                    console.error('Failed to switch to CDN backend:', error);
                    // Continue with local backend if CDN switch fails
                }
            }

            // Set user's language preference
            const userLocalePreference = getUserLocalePreference(currentUser);
            if (userLocalePreference) {
                await i18n.changeLanguage(userLocalePreference);
            }

            hasInitialized.current = true;
        };

        initializeI18n();
    }, [currentUser, isUserAvailable, isSpanishLocaleEnabled]);

    // Handle language changes when user updates their preference
    useEffect(() => {
        if (!hasInitialized.current || !isUserAvailable) {
            return;
        }

        const userLocalePreference = getUserLocalePreference(currentUser);
        if (userLocalePreference && i18n.language !== userLocalePreference) {
            i18n.changeLanguage(userLocalePreference);
        }
    }, [currentUser, isUserAvailable]);

    return null;
};

export default I18nLanguageManager;